import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { MathUtil } from '#layers/base/utils/math'
import * as Sentry from '@sentry/nuxt'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'

export interface Balance {
  available: string
  locked: string
  total: string
  currency: 'XAUT'
  // Bonus wallet (BONUS_XAUT). Present when balance-get returns it; older
  // cached responses may omit them, so treat as optional and default to '0'.
  bonusAvailable?: string
  bonusLocked?: string
  bonusTotal?: string
}

export const useBalanceStore = defineStore('balance', () => {
  const config = useRuntimeConfig()

  // State
  const balance = ref<Balance>({
    available: '0',
    locked: '0',
    total: '0',
    currency: 'XAUT',
    bonusAvailable: '0',
    bonusLocked: '0',
    bonusTotal: '0'
  })
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const available = computed(() => parseFloat(balance.value.available))
  const locked = computed(() => parseFloat(balance.value.locked))
  const total = computed(() => parseFloat(balance.value.total))

  // Bonus wallet getters. The bonus balance is what the player wagers first
  // while a bonus is active; it's surfaced separately in the UI so the two
  // wallets are never conflated. Defaults to 0 when the field is absent.
  const bonusAvailable = computed(() => parseFloat(balance.value.bonusAvailable || '0'))
  const bonusLocked = computed(() => parseFloat(balance.value.bonusLocked || '0'))
  // Derived, never read from the stored `bonusTotal` field: total ≡ available +
  // locked by wallet definition, and the stored field can drift stale when a
  // partial balance update (e.g. a settled-bet broadcast) refreshes available /
  // locked but not total (the `?? existing` merge keeps the old total). Deriving
  // it keeps every surface consistent — the game header (bonusTotal) and the
  // wallet card (bonusAvailable) can no longer disagree for the same wallet.
  const bonusTotal = computed(() => bonusAvailable.value + bonusLocked.value)
  const bonusAvailableFormatted = computed(() => formatXAUT(balance.value.bonusAvailable || '0'))
  const hasBonus = computed(() => bonusAvailable.value > 0)

  const availableFormatted = computed(() =>
    formatXAUT(balance.value.available)
  )

  const lockedFormatted = computed(() =>
    formatXAUT(balance.value.locked)
  )

  const totalFormatted = computed(() =>
    formatXAUT(balance.value.total)
  )

  const hasBalance = computed(() => available.value > 0)

  // True when the withdrawable balance is negative - a chargeback clawback can
  // push a user into an outstanding debt. `hasBalance` stays false (betting is
  // blocked), but this flag lets the UI explain WHY instead of silently
  // dropping the player into demo mode.
  const isOutstanding = computed(() => available.value < 0)

  // Actions
  async function fetchBalance(accessToken: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      // Send X-Session-Id for fast auth (Redis-only, avoids heavy bundle)
      const sessionId = typeof window !== 'undefined'
        ? localStorage.getItem('aurumrun_session_id')
        : null
      const response = await $fetch<Balance>(`${config.public.apiBase}/balance-get`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(sessionId && { 'X-Session-Id': sessionId }),
        },
      })
      balance.value = response
    } catch (err) {
      console.error('[balanceStore] Failed to fetch balance:', err)
      Sentry.captureException(err, { tags: { store: 'balance', action: 'fetchBalance' } })
      error.value = getApiErrorMessage(err, 'Failed to fetch balance')
    } finally {
      isLoading.value = false
    }
  }

  function updateBalance(newBalance: Balance): void {
    // Preserve the bonus wallet fields when the incoming payload omits them.
    // Game WS/HTTP responses (bet placement, cancel, balance_update) carry only
    // the withdrawable wallet (available/locked/total) — replacing the whole
    // object would blank bonusAvailable/Locked/Total and make the bonus balance
    // flicker to 0 mid-play and during the final conversion. A full balance-get
    // response DOES include these fields (even as '0'), so it still wins:
    // `'0' ?? existing` === '0'; only a missing (undefined) field falls back.
    balance.value = {
      ...newBalance,
      bonusAvailable: newBalance.bonusAvailable ?? balance.value.bonusAvailable,
      bonusLocked: newBalance.bonusLocked ?? balance.value.bonusLocked,
      bonusTotal: newBalance.bonusTotal ?? balance.value.bonusTotal,
    }
  }

  function updateAvailable(amount: string): void {
    balance.value.available = amount
    recalculateTotal()
  }

  function updateLocked(amount: string): void {
    balance.value.locked = amount
    recalculateTotal()
  }

  function recalculateTotal(): void {
    balance.value.total = MathUtil.add(balance.value.available || '0', balance.value.locked || '0')
  }

  function clearBalance(): void {
    balance.value = {
      available: '0',
      locked: '0',
      total: '0',
      currency: 'XAUT',
      bonusAvailable: '0',
      bonusLocked: '0',
      bonusTotal: '0'
    }
    error.value = null
  }

  return {
    // State
    balance,
    isLoading,
    error,
    // Getters
    available,
    locked,
    total,
    availableFormatted,
    lockedFormatted,
    totalFormatted,
    hasBalance,
    isOutstanding,
    bonusAvailable,
    bonusLocked,
    bonusTotal,
    bonusAvailableFormatted,
    hasBonus,
    // Actions
    fetchBalance,
    updateBalance,
    updateAvailable,
    updateLocked,
    clearBalance,
  }
})
